<template>
  <v-container
    v-resize="resizeDrawer"
    fluid
    class="template-container"
  >
    <v-overlay :value="isLoading">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>
    <v-row class="content-flex-md-nowrap no-gutters">
      <v-col cols="12">
        <v-alert
          :value="!appUserLoading && document.stage === 'Vault'"
          type="info"
          prominent
          dense
        >
          This document is now in your
          <router-link
            to="/vault"
            class="grey--text text--lighten-3"
          >
            Vault
          </router-link>
          and can't be edited.
        </v-alert>

        <v-alert
          :value="
            !appUserLoading &&
              (document.stage === 'Lawyer' ||
                (document.accountType === 'lawyer' &&
                  document.stage === 'Client Completed')) &&
              !(isLawyer || isAdmin)
          "
          type="info"
          prominent
          dense
        >
          This document has been sent to a lawyer for review and can no longer
          be edited.
        </v-alert>

        <v-alert
          :value="
            !appUserLoading &&
              document.accountType !== 'lawyer' &&
              document.stage === 'Client Completed' &&
              isIndividual
          "
          type="info"
          prominent
          dense
        >
          This document has been sent for review and can no longer be edited.
        </v-alert>

        <!-- not assigned to me but can edit -->
        <!-- TODO BETA bug client can't reassign to themselves... -->
        <v-alert
          :value="
            !appUserLoading &&
              !isAdmin &&
              document.assignedTo !== $store.state.user.uid &&
              couldEdit
          "
          type="info"
          prominent
          dense
        >
          This document isn't currently assigned to you.<br>
          If you would like to edit the document, you can reassign it to
          yourself in the
          <a
            :href="`#${_.kebabCase(metaSectionTitle + '_step')}`"
            class="grey--text text--lighten-3"
            @click.stop.prevent="
              currentSection = sections + 1
              stepperInput(sections + 1)
            "
            v-text="metaSectionTitle"
          />
          section.
        </v-alert>
      </v-col>
    </v-row>
    <v-row class="content-flex-md-nowrap no-gutters">
      <v-col
        v-if="sections && fieldsByName && fieldsByNameId"
        cols="12"
        class="flex-shrink content-col-sm-auto px-xs-only-0 d-flex"
      >
        <portal-source to="titlebarContents">
          <h1 class="title-text">
            {{ document.name }}
          </h1>
        </portal-source>

        <!-- Contents Drawer -->

        <document-help
          v-if="!templateLoading"
          :template="template"
        />

        <v-navigation-drawer
          v-show="showContentsDrawer"
          clipped
          floating
          color="transparent"
          width="220"
          height="auto"
          hide-overlay
          :mobile-breakpoint="$navMobileBreakpoint"
          style="
            position: sticky;
            align-self: flex-start;
            width: 250px;
            min-width: 250px;
            min-height: calc(100vh - 64px - 42px);
            top: 64px;
            transform: translateX(0%) !important;
            max-height: calc(100vh - 64px - 42px);
            z-index: 0;
          "
        >
          <div class="container-document-toc">
            <document-toc
              :sees-triggers="seesTriggers"
              :section-states="sectionStates"
              @click="
                (sectionNum) => {
                  currentSection = sectionNum
                  stepperInput(sectionNum)
                }
              "
            />
          </div>
        </v-navigation-drawer>

        <v-form
          id="docForm"
          ref="form"
          :value="formValid"
        >
          <v-stepper
            v-if="!appUserLoading"
            ref="vstepper"
            v-model="currentSection"
            vertical
            non-linear
            class="do-stepper body-2 ol-stepper"
            style="min-height: 100%"
            @change="stepperInput"
          >
            <!-- TODO use sectionState  -->
            <template v-for="sectionStep in sectionStates">
              <!-- Custom Stepper Sections -->
              <template v-if="sectionStep.isCustom">
                <v-stepper-items
                  :key="`stepper-${sectionStep.stepId}`"
                >
                  <v-stepper-content
                    :key="`stepper-${sectionStep.stepId}`"
                    :step="sectionStep.sectionNum"
                  >
                    <component
                      :is="sectionStep.component"
                      :show-no-data-error="sectionStep.hasError"
                      :document-ref="documentRef"
                      :document="document"
                      @saving="dirty = true"
                      @saved="dirty = false"
                      @hasError="updateHasError(sectionStep, $event)"
                    />
                  </v-stepper-content>
                </v-stepper-items>
              </template>
              <!-- End Custom Stepper Sections -->

              <template
                v-if="!sectionStep.isCustom"
              >
                <v-stepper-items
                  v-if="formDefSectioned[sectionStep.sectionNum]"
                  :key="`content_${sectionStep.sectionNum}`"
                  :set="n = sectionStep.sectionNum"
                >
                  <!-- TODO: Title line-height seems a bit high, pushes this down too much -->
                  <h2
                    v-show="!showContentsDrawer"
                    :id="'header' + n"
                    :key="`header_${sectionStep.sectionNum}`"
                    class="title"
                  >
                    <document-toc-item
                      v-if="!showContentsDrawer"
                      class="mb-3 px-xs-only-2 document-header"
                      style="padding: 0 12px"
                      :section="sectionStates[n - 1]"
                      :num-sections="sectionStates.length"
                      :sees-triggers="seesTriggers"
                      @click="
                        currentSection = n
                        stepperInput(n)
                      "
                    />
                  </h2>
                  <v-stepper-content
                    :id="_.kebabCase(formDefSectioned[n].fields[0].section_heading)"
                    :ref="'content' + n"
                    :step="n"
                    :class="{
                      'fill-height':
                        $vuetify.breakpoint.smAndUp && currentSection === n,
                      'py-0': showContentsDrawer
                    }"
                  >
                    <v-card
                      v-if="n === currentSection"
                      tile
                    >
                      <v-container fluid>
                        <v-row>
                          <template v-for="field in formDefSectioned[n].fields">
                            <template v-if="field.type === 'section'">
                              <v-col
                                v-show="
                                  field.visible &&
                                    field.section_heading &&
                                    showContentsDrawer
                                "
                                :key="field.nameId"
                                :class="field.classes"
                                class="col body-2 pt-xs-only-0 pt-0"
                              >
                                <h2
                                  v-if="field.section_break"
                                  :ref="field.nameId"
                                >
                                  {{ field.section_heading }}
                                </h2>
                                <h3
                                  v-if="!field.section_break"
                                  :ref="field.nameId"
                                >
                                  {{ field.section_heading }}
                                </h3>
                              </v-col>
                              <v-col
                                v-show="field.visible && field.section_text"
                                :key="field.nameId + 'section_text'"
                                :class="field.classes"
                                class="col body-2 pt-xs-only-0 pt-0"
                              >
                                <!-- eslint-disable vue/no-v-html -->
                                <!-- TODO just use css in ol-alert to hide icon on xs -->
                                <v-alert
                                  :ref="field.nameId + 'section_text'"
                                  type="info"
                                  :icon="false"
                                  border="left"
                                  colored-border
                                  class="body-2 mb-1 mt-0"
                                >
                                  <div v-html="field.section_text" />
                                </v-alert>
                              </v-col>
                            </template>

                            <!-- eslint-disable vue/no-v-html -->
                            <div
                              v-else-if="field.type === 'richtext' && field.visible"
                              :key="field.nameId"
                              :class="field.classes"
                              class="col container-form-group"
                            >
                              <v-alert
                                type="info"
                                :icon="false"
                                border="left"
                                colored-border
                                class="body-2 mb-5 mt-1"
                              >
                                <div
                                  v-html="
                                    field.section_text.replace(
                                      /(v-alert.*?\b|info--text)/g,
                                      ''
                                    )
                                  "
                                />
                                <!-- TODO remove alert styling from Formstack defs. Disability Will will need some kind of warning treatment -->
                              </v-alert>
                            </div>

                            <template v-else-if="field.vfield.component">
                              <div
                                v-show="field.visible"
                                :key="field.nameId"
                                :class="field.classes"
                                class="col container-form-group"
                              >
                                <!--       <field-component
                                  :groupify="groupify"
                                  :field-options="fieldOptions"
                                  :field="field"
  :model="model" :can-edit="canEdit"
                                  :vue-instance="_self"
  @valueChanged="({fieldName, fieldNameId}) => valueChanged(fieldName, fieldNameId)"
                                /> -->

                                <component
                                  :is="field.vfield.component"
                                  :id="field.nameId"
                                  :ref="field.nameId"
                                  v-model="model[field.nameId].value"
                                  :label="
                                    field.label + (field.required ? '*' : '')
                                  "
                                  :hint="field.description"
                                  :persistent-hint="
                                    field.description ? true : false
                                  "
                                  :name="field.nameId"
                                  :required="field.required"
                                  :rules="field.rules"
                                  :readonly="field.readonly || !canEdit"
                                  :disabled="field.disabled || !canEdit"
                                  :validate-on-blur="true"
                                  :class="{ 'text-no-wrap': !!field.options }"
                                  v-bind="
                                    field.vfield.binds
                                      ? field.vfield.binds(_self)
                                      : null
                                  "
                                  v-on="{
                                    [field.vfield.event]: () =>
                                      valueChanged(field.name, field.nameId)
                                  }"
                                >
                                  <div
                                    slot="no-data"
                                    class="px-2"
                                    style="max-width: 500px"
                                  >
                                    <span class="error--text caption">
                                      <template
                                        v-if="
                                          field.optionsComputedName &&
                                            fieldOptions(field).length === 0
                                        "
                                      >To enable selection, fill in one of
                                        {{
                                          field.options
                                            .map((option) => option.label)
                                            .join('; ')
                                        }}.</template>
                                      <template
                                        v-else-if="
                                          field.vfield.component !== 'v-combobox'
                                        "
                                      >No matching options found.</template>
                                    </span>
                                  </div>
                                </component>
                              </div>
                            </template>

                            <template v-else-if="field.type === 'calculation'">
                              <div
                                v-show="field.visible"
                                :key="field.nameId"
                                :class="field.classes"
                                class="col container-form-group"
                              >
                                <v-text-field
                                  :id="field.nameId"
                                  :ref="field.nameId"
                                  :label="
                                    field.label + (field.required ? '*' : '')
                                  "
                                  :required="field.required"
                                  :hint="field.description"
                                  :persistent-hint="!!field.description"
                                  :value="field.calculation_fn(visibleValues)"
                                  :readonly="field.readonly || !canEdit"
                                  class="v-input--is-disabled"
                                  :name="field.nameId"
                                  :rules="field.rules"
                                />
                              </div>
                            </template>

                            <!--                           <template v-else-if="field.type === 'radio'">

                            </template> -->

                            <template
                              v-else-if="
                                field.type === 'radio' ||
                                  field.type === 'checkbox'
                              "
                            >
                              <div
                                v-show="field.visible"
                                :key="field.nameId"
                                :class="field.classes"
                                class="col container-form-group"
                              >
                                <v-radio-group
                                  :id="field.nameId"
                                  :ref="field.nameId"
                                  v-model="model[field.nameId].value"
                                  :name="field.nameId"
                                  :label="
                                    !field.hide_label
                                      ? field.label + (field.required ? '*' : '')
                                      : ''
                                  "
                                  :readonly="field.readonly || !canEdit"
                                  :disabled="field.disabled || !canEdit"
                                  :rules="field.rules"
                                  :row="
                                    field.options.length > 1 &&
                                      field.option_layout === 'horizontal'
                                  "
                                  class="ol-selection-group pt-0 mt-0"
                                  :class="{
                                    'v-input--has-value':
                                      model[field.nameId].value &&
                                      model[field.nameId].value.length,
                                    'no-label': field.hide_label,
                                    'single-option-hide-label-not-required':
                                      field.hide_label &&
                                      field.options.length === 1 &&
                                      !field.required
                                  }"
                                  @change="valueChanged(field.name, field.nameId)"
                                >
                                  <div
                                    v-show="!field.hide_label"
                                    slot="label"
                                    class="ol-selection-group-label v-label"
                                    v-text="
                                      field.label + (field.required ? '*' : '')
                                    "
                                  />
                                  <div
                                    slot="default"
                                    class="ol-selection-group-controls"
                                  >
                                    <template v-if="fieldOptionsEmpty(field)">
                                      <span class="error--text caption">
                                        <template v-if="field.optionsComputedName">To enable selection, fill in one of
                                          {{
                                            field.options
                                              .map((option) => option.label)
                                              .join('; ')
                                          }}.</template>
                                        <template v-else>No options found.</template>
                                      </span>
                                    </template>

                                    <template v-if="field.type === 'radio'">
                                      <v-radio
                                        v-for="(option, index) in fieldOptions(
                                          field
                                        )"
                                        :id="field.nameId + '_' + index"
                                        :key="field.nameId + option.label"
                                        :value="option.value"
                                        :label="option.label"
                                        :readonly="field.readonly || !canEdit"
                                        :disabled="field.disabled || !canEdit"
                                        hide-details
                                        class="mt-0"
                                        :validate-on-blur="true"
                                        :class="{
                                          'ol-selected-value':
                                            model[field.nameId].value &&
                                            model[field.nameId].value.includes(
                                              option.value
                                            )
                                        }"
                                      />
                                    </template>

                                    <template v-else>
                                      <v-checkbox
                                        v-for="(option, index) in fieldOptions(
                                          field
                                        )"
                                        :id="field.nameId + '_' + index"
                                        :key="field.nameId + option.label"
                                        v-model="model[field.nameId].value"
                                        hide-details
                                        :label="
                                          option.label +
                                            (field.required &&
                                              field.options.length > 1 &&
                                              field.hide_label
                                              ? '*'
                                              : '')
                                        "
                                        :value="option.value"
                                        :rules="field.rules"
                                        :readonly="field.readonly || !canEdit"
                                        :disabled="field.disabled || !canEdit"
                                        class="mt-0 pt-0"
                                        :validate-on-blur="true"
                                        :class="{
                                          'ol-selected-value':
                                            model[field.nameId].value &&
                                            model[field.nameId].value.includes(
                                              option.value
                                            )
                                        }"
                                        @change="
                                          valueChanged(field.name, field.nameId)
                                        "
                                      />
                                    </template>
                                  </div>
                                </v-radio-group>
                              </div>
                            </template>

                            <template v-else-if="field.type === 'address'">
                              <div
                                v-show="field.visible"
                                :key="field.nameId"
                                class="col container-form-group"
                                :class="field.classes"
                              >
                                <address-field
                                  :id="field.nameId"
                                  :ref="field.nameId + '_search'"
                                  :name="field.nameId"
                                  :label="
                                    field.label + (field.required ? '*' : '')
                                  "
                                  append-icon="mdi-map-marker"
                                  :rules="[
                                    (v) =>
                                      v === model[field.nameId].value ||
                                      'Invalid address.'
                                  ]"
                                  class="hide-messages-if-no-error"
                                  :title="field.description"
                                  :value="model[field.nameId].value"
                                  :required="field.required"
                                  :readonly="field.readonly || !canEdit"
                                  :disabled="field.disabled || !canEdit"
                                  @placechanged="getAddressData"
                                />

                                <v-text-field
                                  :ref="field.nameId"
                                  v-model="model[field.nameId].value"
                                  :title="field.description"
                                  :rules="
                                    field.rules.concat(() =>
                                      !field.visible ||
                                      model[field.name + '_auto_' + field.id]
                                        .value === true || model[field.nameId].value
                                        ? true
                                        : 'Invalid address.'
                                    )
                                  "
                                  :required="field.required"
                                  readonly
                                  class="v-input--is-disabled pt-1"
                                />
                              </div>
                            </template>

                            <template v-if="formDefSectioned[n].type === 'meta'">
                              <div
                                :key="'metaSectionContent'"
                                class="col container-form-group col-12"
                              >
                                <!-- ADVISER-CLIENT-ADVISER-LAWYER Docs -->
                                <v-stepper
                                  v-if="document.accountType === 'adviser'"
                                  v-model="metaSectionStep"
                                  vertical
                                  flat
                                >
                                  <v-stepper-step
                                    step="1"
                                    v-bind="{ editable: isAdmin }"
                                    :complete="metaSectionStep > 1"
                                    :color="
                                      metaSectionStep > 1 ? 'success' : 'primary'
                                    "
                                  >
                                    Adviser
                                  </v-stepper-step>
                                  <v-stepper-content step="1">
                                    <v-card>
                                      <v-card-text v-show="isClient || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Client: '"
                                        />
                                        <p>
                                          Your financial adviser will fill in any
                                          information they have on your behalf and
                                          then forward the document to you to
                                          continue.
                                        </p>
                                      </v-card-text>
                                      <v-divider v-show="isAdmin" />
                                      <v-card-text v-show="isAdviser || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Adviser: '"
                                        />
                                        <p>
                                          Once you've filled in any information
                                          you have for the client, hit Send to
                                          reassign the document to the client to
                                          continue.
                                          <br>
                                          <v-btn
                                            small
                                            color="primary"
                                            :disabled="sending"
                                            :loading="sending"
                                            @click="sendToClient"
                                          >
                                            Send
                                          </v-btn>
                                        </p>
                                      </v-card-text>
                                      <v-divider v-show="isAdmin" />
                                      <v-card-text v-show="isLawyer || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Lawyer: '"
                                        />
                                        <p>
                                          N/A
                                          <!-- TODO BETA always N/A? -->
                                        </p>
                                      </v-card-text>
                                    </v-card>
                                  </v-stepper-content>

                                  <v-stepper-step
                                    step="2"
                                    v-bind="{ editable: isAdmin }"
                                    :complete="metaSectionStep > 2"
                                    :color="
                                      metaSectionStep > 2 ? 'success' : 'primary'
                                    "
                                  >
                                    Client
                                  </v-stepper-step>
                                  <v-stepper-content step="2">
                                    <v-card>
                                      <v-card-text v-show="isClient || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Client: '"
                                        />
                                        <p>
                                          Once you've filled in as much
                                          information as you can, hit Send to
                                          return the document to your financial
                                          adviser. You'll also be asked to approve
                                          the expected lawyer fees and terms.
                                          <br>
                                          <v-btn
                                            small
                                            color="primary"
                                            :disabled="sending"
                                            :loading="sending"
                                            @click="sendToAccountTypeUser"
                                          >
                                            Send
                                          </v-btn>
                                        </p>
                                      </v-card-text>
                                      <v-divider v-show="isAdmin" />
                                      <v-card-text v-show="isAdviser || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Adviser: '"
                                        />
                                        <p>
                                          Once the client has filled in their
                                          information, they'll send the document
                                          back to you for review.
                                        </p>
                                        <p>
                                          <strong>Or</strong>, you can reassign it
                                          if the client has told you they've
                                          finished.
                                          <br>
                                          <!-- TODO ALPHA? Reassign terminology is wrong here, should be something meaning "Progress to Next Stage" / Review / Send to "Me" -->
                                          <v-btn
                                            small
                                            :disabled="sending"
                                            :loading="sending"
                                            @click="sendToAccountTypeUser"
                                          >
                                            Reassign
                                          </v-btn>
                                        </p>
                                      </v-card-text>
                                      <v-divider v-show="isAdmin" />
                                      <v-card-text v-show="isLawyer || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Lawyer: '"
                                        />
                                        <p>
                                          N/A
                                          <!-- TODO BETA always N/A? -->
                                        </p>
                                      </v-card-text>
                                    </v-card>
                                  </v-stepper-content>

                                  <v-stepper-step
                                    step="3"
                                    v-bind="{ editable: isAdmin }"
                                    :complete="metaSectionStep > 3"
                                    :color="
                                      metaSectionStep > 3 ? 'success' : 'primary'
                                    "
                                  >
                                    Adviser Review
                                  </v-stepper-step>
                                  <v-stepper-content step="3">
                                    <v-card>
                                      <v-card-text v-show="isClient || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Client: '"
                                        />
                                        <p>
                                          Once your adviser has reviewed the
                                          information you've provided, they will
                                          then forward your document to an expert
                                          lawyer to review and finalise.
                                        </p>
                                      </v-card-text>
                                      <v-divider v-show="isAdmin" />
                                      <v-card-text v-show="isAdviser || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Adviser: '"
                                        />
                                        <p>
                                          Once you've reviewed the information
                                          provided by the client, hit Send to
                                          Lawyer to forward this document to an
                                          expert lawyer.
                                          <br>
                                          <v-btn
                                            small
                                            @click="sendToClient"
                                          >
                                            Send back to Client
                                          </v-btn>
                                          <v-btn
                                            small
                                            color="primary"
                                            @click="sendToLawyer"
                                          >
                                            Send to Lawyer
                                          </v-btn>
                                        </p>
                                      </v-card-text>
                                      <v-divider v-show="isAdmin" />
                                      <v-card-text v-show="isLawyer || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Lawyer: '"
                                        />
                                        <p>
                                          N/A
                                          <!-- TODO BETA always N/A? -->
                                        </p>
                                      </v-card-text>
                                    </v-card>
                                  </v-stepper-content>

                                  <v-stepper-step
                                    step="4"
                                    v-bind="{ editable: isAdmin }"
                                    :complete="document.stage === 'Vault'"
                                    :color="
                                      document.stage === 'Vault'
                                        ? 'success'
                                        : 'primary'
                                    "
                                  >
                                    Lawyer Review
                                  </v-stepper-step>
                                  <v-stepper-content step="4">
                                    <v-card>
                                      <v-card-text v-show="isClient || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Client: '"
                                        />
                                        <p>
                                          Your expert lawyer is reviewing the
                                          information you've provided and will
                                          contact you to discuss the document in
                                          detail.
                                        </p>
                                      </v-card-text>
                                      <v-divider v-show="isAdmin" />
                                      <v-card-text v-show="isAdviser || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Adviser: '"
                                        />
                                        <p>
                                          The expert lawyer is reviewing the
                                          information provided and will contact
                                          the client to discuss the document in
                                          detail.
                                        </p>
                                      </v-card-text>
                                      <v-divider v-show="isAdmin" />
                                      <v-card-text v-show="isLawyer || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Lawyer: '"
                                        />
                                        <p>
                                          Check all details are correct then hit 'Generate'. The document will be on your dashboard, ready for you to send it to the counterparty for signature.
                                          <br>
                                          <v-btn
                                            small
                                            color="primary"
                                            :loading="downloading"
                                            :disabled="downloading"
                                            @click="download"
                                          >
                                            Generate
                                          </v-btn>
                                        </p>
                                        <p>
                                          Once you've reviewed and completed the
                                          document offline, check the confirmation
                                          box and hit Finalise to start the 'Send
                                          to Vault and Upload' process.
                                          <br>
                                          <v-checkbox
                                            v-model="lawyerReviewedSignedOffline"
                                            :disabled="saving"
                                            label="The document has been reviewed, signed and executed offline."
                                            @change="
                                              toggleLawyerReviewedSignedOffline
                                            "
                                          />
                                          <v-btn
                                            small
                                            color="primary"
                                            :disabled="
                                              !document.lawyerReviewedSignedOffline ||
                                                saving
                                            "
                                            :loading="saving"
                                            @click="finaliseDialog = true"
                                          >
                                            Finalise
                                          </v-btn>
                                        </p>
                                      </v-card-text>
                                    </v-card>
                                  </v-stepper-content>
                                </v-stepper>

                                <!-- INDIVIDUAL Docs -->
                                <v-stepper
                                  v-if="document.accountType === 'individual'"
                                  v-model="metaSectionStep"
                                  vertical
                                >
                                  TODO BETA? This hasn't been coded.
                                  <v-stepper-step
                                    step="1"
                                    v-bind="{ editable: isAdmin }"
                                    :complete="
                                      typeof document.upgradeToLawyerType !==
                                        'undefined'
                                    "
                                    :color="
                                      typeof document.upgradeToLawyerType !==
                                        'undefined'
                                        ? 'success'
                                        : 'primary'
                                    "
                                  >
                                    Lawyer review?
                                  </v-stepper-step>

                                  <v-stepper-content step="1">
                                    <v-card>
                                      <v-card-text v-show="isClient || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Client: '"
                                        />
                                        <div>
                                          <p>
                                            We
                                            <strong>strongly</strong> recommend
                                            getting your document reviewed by one
                                            of our expert lawyers prior to
                                            finalising it.
                                          </p>
                                          <p>
                                            Of course, involving a lawyer
                                            increases the cost of this document,
                                            but it ensures that this document
                                            accurately reflects your wishes
                                            completely and correctly, and also
                                            means you get expert advice on other
                                            documents you might need in order to
                                            fulfil your wishes.
                                          </p>
                                          <!-- TODO MVP show cost -->
                                          <v-radio-group
                                            v-model="upgradeToLawyerType"
                                            :disabled="saving"
                                            :loading="saving"
                                            @change="toggleUpgradeToLawyerType"
                                          >
                                            <v-radio
                                              label="Continue without lawyer review"
                                              :value="false"
                                            />
                                            <v-radio
                                              label="Get an expert lawyer to review this document"
                                              :value="true"
                                            />
                                          </v-radio-group>

                                          <v-btn
                                            small
                                            color="primary"
                                            :disabled="
                                              typeof document.upgradeToLawyerType ===
                                                'undefined'
                                            "
                                            @click="metaSectionStep++"
                                          >
                                            Next
                                          </v-btn>
                                        </div>
                                      </v-card-text>
                                    </v-card>
                                  </v-stepper-content>

                                  <!-- TODO MVP payment stuff should be stored in own collection for security? -->

                                  <v-stepper-step
                                    step="2"
                                    v-bind="{ editable: isAdmin }"
                                    :complete="document.paid"
                                    :color="document.paid ? 'success' : 'primary'"
                                  >
                                    Payment
                                  </v-stepper-step>
                                  <v-stepper-content step="2">
                                    <v-card>
                                      <v-card-text v-show="isClient || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Client: '"
                                        />
                                        <p>// enter payment details</p>
                                        <!-- TODO MVP remove once payment implemented -->
                                        <v-btn
                                          small
                                          :disabled="document.paid"
                                          @click="$set(document, 'paid', true)"
                                        >
                                          Simulate Paid
                                        </v-btn>
                                        <v-btn
                                          small
                                          color="primary"
                                          :disabled="!document.paid"
                                          @click="metaSectionStep++"
                                        >
                                          Next
                                        </v-btn>
                                      </v-card-text>
                                    </v-card>
                                  </v-stepper-content>

                                  <v-stepper-step
                                    step="3"
                                    v-bind="{ editable: isAdmin }"
                                    :complete="document.stage === 'Vault'"
                                    :color="
                                      document.stage === 'Vault'
                                        ? 'success'
                                        : 'primary'
                                    "
                                  >
                                    Generate Document
                                  </v-stepper-step>
                                  <v-stepper-content step="3">
                                    <v-card>
                                      <v-card-text v-show="isClient || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Client: '"
                                        />
                                        <p>
                                          Once you've filled in all relevant
                                          information, you can generate a copy of
                                          the document to review and sign.
                                          <br>
                                          <v-btn
                                            small
                                            color="primary"
                                            :disabled="
                                              !document.paid || downloading
                                            "
                                            :loading="downloading"
                                            @click="download"
                                          >
                                            Generate
                                          </v-btn>
                                        </p>
                                        <p>
                                          Once you've reviewed and signed the
                                          document offline, check the confirmation
                                          box and hit Finalise to start the 'Send
                                          to Vault and Upload' process.
                                          <!-- TODO MVP link to What is Vault help -->
                                          <br>
                                          <v-checkbox
                                            v-model="
                                              individualReviewedSignedOffline
                                            "
                                            label="I've reviewed and signed the generated document."
                                            :disabled="saving"
                                            @change="
                                              toggleIndividualReviewedSignedOffline
                                            "
                                          />
                                          <v-btn
                                            small
                                            color="primary"
                                            :disabled="
                                              !document.individualReviewedSignedOffline ||
                                                saving
                                            "
                                            :loading="saving"
                                            @click="finaliseDialog = true"
                                          >
                                            Finalise
                                          </v-btn>
                                        </p>
                                      </v-card-text>
                                    </v-card>
                                  </v-stepper-content>
                                </v-stepper>

                                <!-- LAWYER-CLIENT-LAWYER Docs -->
                                <v-stepper
                                  v-if="document.accountType === 'lawyer'"
                                  v-model="metaSectionStep"
                                  vertical
                                >
                                  <v-stepper-step
                                    step="1"
                                    v-bind="{ editable: isAdmin }"
                                    :complete="metaSectionStep > 1"
                                    :color="
                                      metaSectionStep > 1 ? 'success' : 'primary'
                                    "
                                  >
                                    Lawyer
                                  </v-stepper-step>
                                  <v-stepper-content step="1">
                                    <v-card>
                                      <v-card-text v-show="isClient || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Client: '"
                                        />
                                        <p>
                                          Your lawyer will fill in any information
                                          they have on your behalf and then
                                          forward the document to you to continue.
                                        </p>
                                      </v-card-text>
                                      <v-divider v-show="isAdmin" />
                                      <v-card-text v-show="isLawyer || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Lawyer: '"
                                        />
                                        <p>
                                          Once you've filled in any information
                                          you have for the client, hit Send to
                                          reassign the document to the client to
                                          continue.
                                          <br>
                                          <v-btn
                                            small
                                            color="primary"
                                            :disabled="sending"
                                            :loading="sending"
                                            @click="sendToClient"
                                          >
                                            Send
                                          </v-btn>
                                        </p>
                                      </v-card-text>
                                    </v-card>
                                  </v-stepper-content>

                                  <v-stepper-step
                                    step="2"
                                    v-bind="{ editable: isAdmin }"
                                    :complete="metaSectionStep > 2"
                                    :color="
                                      metaSectionStep > 2 ? 'success' : 'primary'
                                    "
                                  >
                                    Client
                                  </v-stepper-step>
                                  <v-stepper-content step="2">
                                    <v-card>
                                      <v-card-text v-show="isClient || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Client: '"
                                        />
                                        <p>
                                          Once you've filled in as much
                                          information as you can, hit Send to
                                          return the document to your lawyer.
                                          <br>
                                          <v-btn
                                            small
                                            color="primary"
                                            :disabled="sending"
                                            :loading="sending"
                                            @click="sendToAccountTypeUser"
                                          >
                                            Send
                                          </v-btn>
                                        </p>
                                      </v-card-text>
                                      <v-divider v-show="isAdmin" />
                                      <v-card-text v-show="isLawyer || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Lawyer: '"
                                        />
                                        <p>
                                          Once the client has filled in their
                                          information, they'll send the document
                                          back to you for review and finalisation.
                                        </p>
                                        <p>
                                          <strong>Or</strong>, you can reassign it
                                          if the client has told you they've
                                          finished.
                                          <br>
                                          <v-btn
                                            small
                                            :disabled="sending"
                                            :loading="sending"
                                            @click="sendToAccountTypeUser"
                                          >
                                            Reassign
                                          </v-btn>
                                        </p>
                                      </v-card-text>
                                    </v-card>
                                  </v-stepper-content>

                                  <v-stepper-step
                                    step="3"
                                    v-bind="{ editable: isAdmin }"
                                    :complete="document.stage === 'Vault'"
                                    :color="
                                      document.stage === 'Vault'
                                        ? 'success'
                                        : 'primary'
                                    "
                                  >
                                    Lawyer Review
                                  </v-stepper-step>
                                  <v-stepper-content step="3">
                                    <v-card>
                                      <v-card-text v-show="isClient || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Client: '"
                                        />
                                        <p>
                                          Your expert lawyer is reviewing the
                                          information you've provided and will
                                          contact you to discuss the document in
                                          detail.
                                        </p>
                                      </v-card-text>
                                      <v-divider v-show="isAdmin" />
                                      <v-card-text v-show="isLawyer || isAdmin">
                                        <span
                                          v-show="isAdmin"
                                          class="font-weight-bold"
                                          v-html="'Lawyer: '"
                                        />
                                        <p>
                                          Check all details are correct then hit 'Generate'. The document will be on your dashboard, ready for you to send it to the counterparty for signature.
                                          <br>
                                          <!-- TODO BETA? Should be a two step process? Generate starts the process and then gives a download link? -->
                                          <v-btn
                                            small
                                            color="primary"
                                            :loading="downloading"
                                            :disabled="downloading"
                                            @click="download"
                                          >
                                            Generate
                                          </v-btn>
                                        </p>
                                        <p>
                                          Once you've reviewed and completed the
                                          document offline, check the confirmation
                                          box and hit Finalise to start the 'Send
                                          to Vault and Upload' process.
                                          <br>
                                          <v-checkbox
                                            v-model="lawyerReviewedSignedOffline"
                                            :disabled="saving"
                                            label="The document has been reviewed, signed and executed offline."
                                            @change="
                                              toggleLawyerReviewedSignedOffline
                                            "
                                          />
                                          <v-btn
                                            small
                                            color="primary"
                                            :disabled="
                                              !document.lawyerReviewedSignedOffline ||
                                                saving
                                            "
                                            :loading="saving"
                                            @click="finaliseDialog = true"
                                          >
                                            Finalise
                                          </v-btn>
                                        </p>
                                      </v-card-text>
                                    </v-card>
                                  </v-stepper-content>
                                </v-stepper>

                                <!-- SINGLEUSER - COUNTERPARTY Docs -->
                                <v-stepper
                                  v-if="
                                    document.accountType === 'singleUser' ||
                                      document.accountType === 'standardBusiness'
                                  "
                                  v-model="metaSectionStep"
                                  vertical
                                  flat
                                >
                                  <v-stepper-content step="1">
                                    <v-card
                                      tile
                                      elevation="0"
                                    >
                                      <v-card-text
                                        v-show="isFirstParty || isAdmin"
                                      >
                                        <p>
                                          <v-alert
                                            v-if="hasIncompleteSections"
                                            type="error"
                                            outlined
                                          >
                                            <p>You have not yet correctly filled out all sections to generate the document.</p>
                                            <p>
                                              Incomplete sections:
                                              <ul>
                                                <li
                                                  v-for="section in incompleteSections"
                                                  :key="section.stepId"
                                                >
                                                  {{ section.sectionHeading }}
                                                </li>
                                              </ul>
                                            </p>
                                          </v-alert>
                                          Check all details are correct then hit 'Generate'. The document will be on your dashboard, ready for you to send it to the counterparty for signature.
                                          <br><br>
                                          <v-btn
                                            small
                                            color="primary"
                                            :loading="downloading"
                                            :disabled="downloading || hasIncompleteSections"
                                            @click="download"
                                          >
                                            Generate
                                          </v-btn>
                                        </p>
                                      </v-card-text>
                                    </v-card>
                                  </v-stepper-content>
                                </v-stepper>
                              </div>
                            </template>

                            <!--<template v-else> <!- Unknown field type->
                          <div v-if="!(field.type === 'section' && (field.section_heading === '' || field.section_text === '')) && field.type !== 'name'">
                            {{ logger(field) }}
                          </div>
                            </template>-->
                          </template>
                        </v-row>
                      </v-container>
                    </v-card>
                  </v-stepper-content>
                </v-stepper-items>
              </template>
            </template>
          </v-stepper>
        </v-form>
      </v-col>

      <v-col
        v-if="false"
        col="12"
        class="content-col-md-3 content-col-lg-2 content-col-xl-1"
        style="border: 1px solid blue"
      >
        <expert-lawyers
          :document-stage="document.stage"
          :template-id="document.templateId"
          @lawyerAssistanceClicked="lawyerAssistanceClicked"
        />
      </v-col>

      <v-dialog
        v-model="finaliseDialog"
        persistent
        max-width="500px"
      >
        <v-card>
          <v-card-title class="column align-start pb-0">
            Finalise Document
          </v-card-title>
          <v-card-text>
            <p>Finalising a document is a two-step process:</p>
            <ol>
              <li>Send the document to the Vault.</li>
              <li>Go to the Vault and upload signed copy.</li>
            </ol>
            <p />
            <p>
              Once the document has been sent to the Vault, you'll be redirected
              so you can upload the signed copy.
            </p>
            <v-alert
              type="info"
              outlined
              :value="true"
            >
              An unsigned version will remain in the Vault until you complete
              step 2.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              small
              text
              :disabled="vaulting"
              @click="finaliseDialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              small
              :disabled="vaulting"
              :loading="vaulting"
              @click="finalise"
            >
              Send to Vault
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="invalidConfirmDialog"
        persistent
        max-width="500px"
      >
        <v-card>
          <v-card-title>Confirm</v-card-title>
          <v-card-text>
            <p>{{ invalidText }}</p>
            <p>Continue anyway?</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              small
              @click="
                invalidConfirmDialog = false
                invalidConfirmDialogResolve(false)
              "
            >
              No
            </v-btn>
            <v-btn
              small
              color="primary"
              @click="
                invalidConfirmDialog = false
                invalidConfirmDialogResolve(true)
              "
            >
              Yes
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>

    <v-footer
      fixed
      inset
      :style="'margin-left: ' + ($store.state.drawer ? $navWidth + 'px' : '0')"
    >
      <v-menu
        v-if="sections && fieldsByName && fieldsByNameId"
        v-model="tocOpen"
        max-height="90vh"
      >
        <template #activator="{ on }">
          <v-btn
            v-show="!showContentsDrawer"
            text
            small
            class="mx-1 col-3 col-sm-auto flex-shrink"
            color="secondary"
            v-on="on"
          >
            <v-icon>mdi-table-of-contents</v-icon>TOC
          </v-btn>
        </template>

        <document-toc
          :sees-triggers="seesTriggers"
          class="white"
          :section-states="sectionStates"
          @click="
            (sectionNum) => {
              currentSection = sectionNum
              stepperInput(sectionNum)
              tocOpen = false
            }
          "
        />
      </v-menu>

      <div>
        <v-btn
          x-large
          color="primary"
          :to="{name: 'dashboard'}"
        >
          <v-icon>
            bx-chevron-left
          </v-icon>
          Complete later
        </v-btn>
        <span class="info--text">
          {{ dirty ? 'Saving...' : 'Saved' }}
          <v-icon>mdi-check</v-icon>
        </span>
      </div>

      <div class="nav-btn">
        <v-btn
          :disabled="currentSection <= MIN_SECTION"
          text
          x-large
          color="primary"
          outlined
          @click="previousStep(currentSection)"
        >
          Back
        </v-btn>

        <v-btn
          :disabled="currentSection >= sections"
          x-large
          color="primary"
          @click="nextStep(currentSection)"
        >
          Next
        </v-btn>
      </div>
    </v-footer>
  </v-container>
</template>

<script>
import { firebase, fsdb, auth } from '@/firebase'

import kebabCase from 'lodash/kebabCase'
import startCase from 'lodash/startCase'
import isEqual from 'lodash/isEqual'
import differenceWith from 'lodash/differenceWith'
import FormAssetInput from './FormAssetInput.vue'
import ConfirmPartyDetails from './ConfirmPartyDetails.vue'
import { canWriteDocument, getContentAssetData } from '@/dataAccess/documents'

const _ = { kebabCase, startCase, isEqual, differenceWith }

const olFields = {
  OlAutocomplete: () => import('@/components/oluetify/OlAutocomplete.vue'),
  OlCombobox: () => import('@/components/oluetify/OlCombobox.vue'),
  OlTextField: () => import('@/components/oluetify/OlTextField.vue'),
  OlTextarea: () => import('@/components/oluetify/OlTextarea.vue'),
  AddressField: () => import('@/components/utils/AddressField.vue')
}

const DocumentToc = () => import('./DocumentToc.vue')
const DocumentTocItem = () => import('./DocumentTocItem.vue')
const DocumentHelp = () => import('./DocumentHelp.vue')

const MIN_SECTION = -1

const currentSectionWatcher = {
  currentSection: function (n, oldN) {
    if (n in this.formDefSectioned) {
      this.formDefSectioned[n].visited = true
    }
    if (oldN in this.formDefSectioned) {
      this.formDefSectioned[oldN].departed = true
      this.validateStepper(oldN)
    }
  }
}

// const watchers = {...dynamicOptionFieldsSources, ...currentSectionWatcher}
const watchers = { ...currentSectionWatcher }

const ASSET_DATA_STEP = 'asset-data-step'
const CONFIRM_PARTY_DETAILS_STEP='confirm-party-details-step'

const CUSTOM_STEPPERS = [
  {
    isCustom: true,
    component: 'confirm-party-details',
    stepId: CONFIRM_PARTY_DETAILS_STEP,
    sectionHeading: 'Confirm Party Details',
    isCustom: true,
    complete: false,
    hasError: false,
    hasTrigger: false,
    isActive: false,
    sectionNum: -1
  },
  {
    isCustom: true,
    component: 'form-asset-input',
    stepId: ASSET_DATA_STEP,
    sectionHeading: 'Asset Data',
    isCustom: true,
    complete: false,
    hasError: false,
    hasTrigger: false,
    isActive: false,
    sectionNum: 0
  },
]

const FINALISE_STEP_ID = 'review-finalise-document-step'

export default {
  name: 'Form',
  components: {
    ...olFields,
    ConfirmPartyDetails,
    DocumentToc,
    DocumentTocItem,
    DocumentHelp,
    FormAssetInput
  },
  props: {
    documentRef: { type: Object, default: () => {} },
    documentId: { type: String, default: '' },
    document: { type: Object, default: () => {} },
    documentHasData: { type: Boolean, default: false },
    formDefWithBehaviours: { type: Object, default: () => {} },
    computeds: { type: Object, default: () => {} },
    initialMetaSectionStep: { type: Number, default: 0 },
    clientUser: { type: Object, default: () => {} }
  },
  data: function () {
    return {
      MIN_SECTION,
      currentSection: MIN_SECTION,
      customSteppers: {
        [ASSET_DATA_STEP]: {
          complete: false,
          hasError: false
        },
        [CONFIRM_PARTY_DETAILS_STEP]: {
          complete: false,
          hasError: false
        }
      },
      signed: false,
      paid: false,
      dirty: false,
      generating: false,
      vaulting: false,
      saving: false,
      sending: false,
      downloading: false,
      reassignDialog: false,
      model: Object.assign({}, this.formDefWithBehaviours.form.model),
      sections: this.formDefWithBehaviours.form.sections,
      saveErrors: 0,
      invalidConfirmDialog: false,
      invalidText: 'One or more items are invalid or incomplete.',
      invalidConfirmDialogResolve: null,
      /* metaSection: { title: 'TITLE', html: '<p>Content</p>' }, */
      metaSectionStep: this.initialMetaSectionStep,
      finaliseDialog: false,
      lawyerReviewedSignedOffline: !!this.document.lawyerReviewedSignedOffline, // TODO MVP combine these two?
      individualReviewedSignedOffline:
        !!this.document.individualReviewedSignedOffline,
      upgradeToLawyerType: this.document.upgradeToLawyerType,
      templateLoading: true,
      isNewDocument: true,
      showContentsDrawer: null,
      tocOpen: false,
      isLoading: false
    }
  },
  computed: {
    mainNavWidth() {
      return this.$navWidth
    },
    metaSectionTitle() {
      if (this.isIndividual) {
        return 'Completing your document'
      }
      // TODO BETA Store stages against doc so we can define what should happen? Need to improve Stage & Status definitions and architecture
      if (this.document.accountType === this.assignedToRole) {
        return 'Review & Manage Document'
      }
      return 'Review & Finalise Document'
    },
    isClient() {
      return this.document.client === this.$store.state.user.uid
    },
    isFirstParty() {
      return this.document.createdBy === this.$store.state.user.uid
    },
    incompleteSections() {
      return this.sectionStates.filter(({ complete, stepId }) => {
        if (stepId === FINALISE_STEP_ID) {
          return false
        }
        return !complete
      })
    },
    hasIncompleteSections() {
      return this.incompleteSections.length > 0
    },
    sectionStates() {
      const formStackStates = Array.from(Array(this.sections).keys()).map((sectionIndex) => {
        const sectionNum = sectionIndex + 1
        const formDefSection = this.formDefSectioned[sectionNum]
        const sectionHeading = formDefSection.fields[0].section_heading
        return {
          isCustom: false,
          sectionNum: sectionNum,
          sectionHeading: sectionHeading,
          isActive: this.currentSection === sectionNum,
          stepId: this._.kebabCase(sectionHeading + '_step'),
          hasError:
            formDefSection &&
            formDefSection.rules.some((rule) => rule() !== true),
          hasTrigger: sectionNum in this.sectionsTriggered,
          complete:
            formDefSection &&
            (formDefSection.type || '') !== 'meta' &&
            (sectionNum in this.sectionProgress
              ? this.sectionProgress[sectionNum].progress === 100
              : true)
        }
      })

      const custom_steppers = CUSTOM_STEPPERS.map((sectionStep) => {
        return ({
          ...sectionStep,
          ...this.customSteppers[sectionStep.stepId],
          isActive: sectionStep.sectionNum===this.currentSection
        })
      })

      return [ ...custom_steppers, ...formStackStates ]
    },
    seesTriggers() {
      return this.isLawyer || this.isAdmin
    },
    metaSection() {
      return Object.values(this.formDefSectioned).find(
        (section) => section.type === 'meta'
      )
    }
  },
  watch: {
    ...watchers,
    metaSectionTitle: {
      handler: function (newVal, oldVal) {
        this.metaSection.fields[0].section_heading = this.metaSectionTitle
      },
      immediate: true
    },
    initialMetaSectionStep: function (newVal, oldVal) {
      if (newVal !== this.metaSectionStep) {
        this.metaSectionStep = newVal
      }
    },
    'document.lawyerReviewedSignedOffline': {
      handler: function (newVal, oldVal) {
        this.lawyerReviewedSignedOffline = !!newVal
      },
      immediate: true
    },
    'document.upgradeToLawyerType': {
      handler: function (newVal, oldVal) {
        this.upgradeToLawyerType = newVal
      },
      immediate: true
    }
  },
  beforeCreate() {
    this._ = _
    this.mapStates(['appUser', 'appUserLoading'])

    this.formDef = this.$options.propsData.formDefWithBehaviours.form.formDef
    this.formDefSectioned =
      this.$options.propsData.formDefWithBehaviours.form.formDefSectioned

    this.$options.computed = {
      ...this.$options.computed,
      ...this.$options.propsData.computeds
    }

    // watch the dynamic fields and clear dependant fields' values and revalidate when changes occur
    const computedsWatchers = Object.keys(
      this.$options.propsData.computeds
    ).reduce((acc, key) => {
      acc[key] = (newVal, oldVal) => {
        // TODO BETA debouncer?

        const diff = _.differenceWith(oldVal, newVal, _.isEqual)

        // if it's just a value being added, don't proceed
        if (!diff.length) {
          return
        }

        const validOptions = newVal

        // fields that use this computed
        const fields = this.formDef.fields.filter(
          (field) =>
            field.optionsComputedName && field.optionsComputedName === key
        )
        fields.forEach((field) => {
          var modelValue = this.model[field.nameId].value

          var isArray = true
          if (Array.isArray(modelValue) === false) {
            isArray = false
            modelValue = [modelValue]
          }

          // if the model value is empty/null, don't proceed
          if (isArray && modelValue.length === 0) {
            return
          }
          if (modelValue[0] === null) {
            return
          }

          if (
            !modelValue.every((val) =>
              validOptions.some((option) => option.value === val)
            )
          ) {
            if (isArray) {
              this.model[field.nameId].value = modelValue.filter((value) =>
                validOptions.some((option) => option.value === value)
              )
            } else {
              this.model[field.nameId].value = null
            }

            // trigger changes and revalidate
            /* const component = this.$refs[field.nameId][0]
            component.$emit('input', this.model[field.nameId].value)
            component.$emit('change', this.model[field.nameId].value)
            this.$nextTick(() => {
              component.validate()
            }) */
          }
        })
      }
      return acc
    }, {})

    // when a 'none of the above' option is checked, uncheck the other options
    // TODO remove none of the above options from templates.
    const noneOfTheAbovesWatchers = this.formDef.fields
      .filter(
        (field) =>
          field.type === 'checkbox' &&
          field.options.some((option) => option.value === 'none of the above')
      )
      .reduce((acc, field) => {
        acc['model.' + field.nameId + '.value'] = (newVal, oldVal) => {
          if (newVal.includes('none of the above') && newVal.length > 1) {
            this.model[field.nameId].value = ['none of the above']

            // trigger changes and revalidate
            const component = this.$refs[field.nameId][0]
            component.$emit('input', this.model[field.nameId].value)
            component.$emit('change', this.model[field.nameId].value)
          }
          this.checkboxValidate(field.nameId)
        }
        return acc
      }, {})

    this.$options.watch = {
      ...this.$options.watch,
      ...computedsWatchers,
      ...noneOfTheAbovesWatchers
    }

    this.$options.computed.visibleValues = function () {
      if (!this.formDef) {
        return null
      }
      return this.formDef.fields.reduce((acc, field, index) => {
        acc[field.nameId] = field.visible
          ? this.model[field.nameId]
          : { value: null }
        return acc
      }, {})
    }
    this.$options.computed.inputFields = function () {
      if (!this.formDef) {
        return null
      }
      return this.formDef.fields.filter((field) => {
        if (field.nameId.indexOf('richtext') === 0) {
          return false
        }
        if (field.nameId.indexOf('section') === 0) {
          return false
        }
        if (field.type === 'name') {
          return false
        }
        return true
      })
    }
    this.$options.computed.sectionsTriggered = function () {
      const triggeredSections = this.formDef.fields
        .filter((field) => field.isTriggered)
        .reduce((acc, field) => {
          acc[field.section] = true
          return acc
        }, {})
      return triggeredSections
    }
    this.$options.computed.sectionProgress = function () {
      if (!this.formDef) {
        return null
      }
      const sectionProgress = this.inputFields.reduce((acc, field) => {
        var sectionProg = acc[field.section] || {
          totalValues: 0,
          nullValues: 0,
          progress: 100
        }
        if (field.visible && field.subSectionVisible && field.required) {
          sectionProg.totalValues++
          if (
            this.model[field.nameId].value == null ||
            this.model[field.nameId].value === '' ||
            this.model[field.nameId].value.length === 0
          ) {
            sectionProg.nullValues++
          }
          sectionProg.progress = Math.round(
            (sectionProg.totalValues - sectionProg.nullValues) /
              sectionProg.totalValues /
              0.01
          )
        }
        acc[field.section] = sectionProg
        return acc
      }, {})
      return sectionProgress
    }
    this.$options.computed.progress = function () {
      if (!this.formDef) {
        return null
      }

      const values = Object.values(this.sectionProgress).reduce(
        (acc, section) => ({
          totalValues: acc.totalValues + section.totalValues,
          nullValues: acc.nullValues + section.nullValues
        }),
        { totalValues: 0, nullValues: 0 }
      )

      /*       var nullValues = 0
      var totalValues = 0

      this.inputFields.forEach((field, index) => {
        if (field.visible && field.subSectionVisible) {
          totalValues++

          if (this.model[field.nameId].value == null || this.model[field.nameId].value === '') {
            nullValues++
          }
        }
      })
 */
      return Math.round(
        (values.totalValues - values.nullValues) / values.totalValues / 0.01
      )
    }

    this.$options.computed.assignedToRole = function () {
      if (this.document.client === this.document.assignedTo) {
        return 'client'
      }
      if (this.document.roles) {
        const assignedToEntry = Object.entries(this.document.roles).find(
          ([role, uid]) => uid === this.document.assignedTo
        )
        if (assignedToEntry) {
          let [assignedToRole, uid] = assignedToEntry // eslint-disable-line no-unused-vars
          return assignedToRole
        } else {
          return this.$store.state.claims.accountType
        }
      } else {
        let assignedToUser =
          this.$store.getters.usersById[this.document.assignedTo]
        if (typeof assignedToUser !== 'undefined') {
          return assignedToUser.accountType
        }
      }
    }

    this.$options.computed.canEdit = function () {
      if (this.document.stage === 'Vault') {
        return false
      }
      if (this.isAdmin) {
        return true
      }
      if (this.document.assignedTo !== this.$store.state.user.uid) {
        return false
      }
      const { user, account } = this.$store.state
      return canWriteDocument(this.document, { user, account, isAccountAdmin: this.isAccountAdmin })
    }

    this.$options.computed.couldEdit = function () {
      if (this.document.stage === 'Vault') {
        return false
      }
      if (this.isAdmin) {
        return true
      }
      if (this.document.stage === 'Lawyer' && !this.isLawyer) {
        return false
      }
      if (this.document.stage === 'Client Completed' && this.isIndividual) {
        return false
      }
      if (
        this.document.accountType === 'lawyer' &&
        this.document.stage === 'Client Completed' &&
        !this.isLawyer
      ) {
        return false
      }
      const { user, account } = this.$store.state
      return canWriteDocument(this.document, { user, account, isAccountAdmin: this.isAccountAdmin })
    }

    this.$options.computed.formValid = function () {
      return !this.sectionStates.some((section) => section.hasError)
    }

    this.mapGetters([
      'isIndividual',
      'isAdviser',
      'isLawyer',
      'isAdmin',
      'isAccountAdmin',
      'usersById'
    ])
  },
  async created() {
    // declared here so they're non-reactive
    this.fieldsByName = this.formDefWithBehaviours.fieldsByName
    this.fieldsByNameId = this.formDefWithBehaviours.fieldsByNameId

    // TODO error handling
    this.$binding(
      'template',
      fsdb.collection('templates').doc(this.document.templateId)
    ).then(async () => {
      this.templateLoading = false
    })
  },
  async mounted() {
    this.isNewDocument =
      'new' in this.$route.params && this.$route.params.new === 'new'

    const calcFields = this.formDef.fields.filter(
      (field) => field.type === 'calculation'
    )
    calcFields.forEach((field) => {
      this.model[field.nameId].value = field.calculation_fn(this.visibleValues)
    })

    const currentSection = this.formDefSectioned[this.currentSection]

    if (currentSection && 'fields' in currentSection) {
      const lastField = currentSection.fields[currentSection.fields.length - 1]

      const lastFieldNameId = lastField.nameId

      // wait for last field to have been created then validateSteppers
      const checkLastField = () => {
        const fieldExists = lastFieldNameId in this.$refs
        if (fieldExists) {
          clearInterval(checkLastFieldIntervalId)
          this.validateStepper(this.currentSection)
        }
      }

      /* await this.$nextTick()
    // this.validateSteppers(true)
    this.$refs.form.validate()

    this.stepperInput(this.currentSection)
 */
      const checkLastFieldIntervalId = setInterval(checkLastField, 250)
    }

    for (let sectionNum in this.formDefSectioned) {
      let section = this.formDefSectioned[sectionNum]
      let ruleNum = 1
      let rules = section.fields
        .filter((field) => field.rules)
        .map((field) =>
          field.rules.map(
            (rule) => () =>
              (this.isNewDocument && !section.departed) ||
              rule(this.model[field.nameId].value)
          )
        )
        .flat()
      section.rules = [...rules]
    }
  },
  methods: {
    updateHasError(sectionState, value) {
      if (sectionState.isCustom) {
        this.customSteppers[sectionState.stepId].hasError = value
        this.customSteppers[sectionState.stepId].complete = !value
      }
    },
    fieldOptions(field) {
      const options = this[field.optionsComputedName] || field.options
      return options.filter((option) => option.label.length)
    },
    fieldOptionsEmpty(field) {
      return this.fieldOptions(field).length === 0
    },
    stepperInput: async function (step) {
      await this.$nextTick()

      if (Object.keys(this.formDefSectioned).includes(step)) {
        const contentElem = this.$refs['content' + step][0].$el
        contentElem.firstChild.style.height = 'auto'
        this.$vuetify.goTo('#header' + step, { offset: 12 })
      }

      this.isAssetData()
      this.validateStepper(step)
      this.savePosition(step)
    },
    savePosition(step) {
      this.$localStorage.set(this.documentId, step)
    },
    checkboxValidate: function (fieldNameId) {
      this.$nextTick(() => {
        this.$refs[fieldNameId][0].$children.forEach((component) => {
          /* TODO this might actually need to be .$childrened becaused of wrappers */
          component.validate && component.validate()
        })
        this.$refs[fieldNameId][0].validate()
      })
    },
    /**
     * When the location found
     * @param {Object} addressData Data of the found location
     * @param {Object} placeResultData PlaceResult object
     * @param {String} id Input container ID
     */
    getAddressData: function (formattedAddress, id) {
      const field = this.fieldsByNameId[id]
      this.model[id].value = formattedAddress

      const autoField =
        this.fieldsByName[this.fieldsByNameId[id].name + '_auto']
      this.model[autoField.nameId].value = true

      this.$refs[id + '_search'][0].validate()

      this.valueChanged(field.name, id)
      this.valueChanged(autoField.name, autoField.nameId)
    },
    // TODO BETA make it so single address line will only accept GMaps valid addresses. Switch to manual if not.
    nextStep(n) {
      if (n === this.sections + 1) {
        this.currentSection = 1
      } else {
        this.currentSection = n + 1
      }
      this.stepperInput(this.currentSection)
    },
    previousStep(n) {
      if (n < MIN_SECTION) {
        this.currentSection = this.sections
      } else {
        this.currentSection = n - 1
      }
      this.stepperInput(this.currentSection)
    },
    validateStepper(stepperIndex, isLoad) {
      /* if it's a new document, don't validate the current section unless it's already been visited previously.
        this doesn't affect the TOC indicators, just whether to show the field validation errors when switching to a section */
      if (
        this.isNewDocument &&
        !(
          stepperIndex === this.currentSection &&
          this.formDefSectioned[this.currentSection] &&
          this.formDefSectioned[this.currentSection].departed
        )
      ) {
        return
      }

      if (!this.documentHasData) {
        return
      }

      this.$refs.form.validate()
    },
    validateSteppers(isLoad) {
      /* TODO whole thing should be unnecessary now */

      isLoad = isLoad || false
      if (this.isNewDocument) {
        return
      }
      if (!this.documentHasData) {
        return
      }

      var currentSection = this.currentSection
      this.$nextTick(() => {
        Object.entries(this.formDefSectioned).forEach(
          ([sectionIndex, section]) => {
            this.validateStepper(sectionIndex, isLoad)
            section.visited = true
          }
        )
        this.$nextTick(() =>
          this.$nextTick(() => {
            this.currentSection = currentSection
          })
        )
      })
    },
    async valueChanged(fieldName, fieldNameId) {
      this.dirty = true

      const contentElem = this.$refs['content' + this.currentSection][0].$el
      contentElem.firstChild.style.height = 'auto'
      try {
        let modelValue = this.model[fieldNameId].value

        // if the value is an array, sort it based on the original options order
        if (Array.isArray(modelValue)) {
          let field = this.fieldsByNameId[fieldNameId]
          let options =
            this[field.optionsComputedName] ||
            field.options.filter((option) => option.label.length)
          modelValue = options
            .filter((option) =>
              this.model[fieldNameId].value.includes(option.value)
            )
            .map((option) => option.value)
        }

        const metadata = {
          modified: firebase.firestore.FieldValue.serverTimestamp(),
          modifiedBy: this.$store.state.user.uid,
          progress: this.progress,
          status: 'Editing'
        }

        var batch = fsdb.batch()
        batch.update(this.documentRef.collection('content').doc('data'), {
          [fieldName]: modelValue
        })
        batch.update(this.documentRef, metadata)
        await batch.commit()

        this.dirty = false
      } catch (e) {
        // TODO BETA - saveErrors currently does nothing. Intended to be for a 'having trouble connecting...'
        this.saveErrors = this.saveErrors + 1
        this.$Sentry.captureException(e)
      }
    },
    getFormData() {
      var data = {}

      data = this.formDef.fields.reduce((data, field, index) => {
        if (field.name && field.visible) {
          data[field.name] = this.model[field.nameId].value
        }
        return data
      }, {})

      /* TODO BETA Refactor addresses to Gmaps auto is its own field, so it doesn't get overwritten
         on save when there's a manual entry.
         Remove the below auto = false when that's done.
      */

      this.formDef.fields
        .filter(
          (field) =>
            /address.*_addressManualEntry/.test(field.name) &&
            this.model[field.nameId].value &&
            this.model[field.nameId].value.includes('manualEntry')
        )
        .sort((a, b) => +a.sort - +b.sort)
        .forEach((field) => {
          const addressFieldName = field.name.replace('_addressManualEntry', '')
          const autoFieldName = field.name.replace(
            '_addressManualEntry',
            '_auto'
          )
          data[autoFieldName] = false
          var address = ''
          const address_manualFieldName =
            addressFieldName + '_address_manual_' + field.id // eslint-disable-line camelcase
          if (address_manualFieldName in this.model) {
            // eslint-disable-line camelcase
            address += this.model[address_manualFieldName].value
          } else {
            address +=
              this.model[addressFieldName + '_addressLine1_' + field.id].value +
              ', '
            address += this.model[
              addressFieldName + '_addressLine2_' + field.id
            ].value
              ? this.model[addressFieldName + '_addressLine1_' + field.id]
                .value + ', '
              : ''
            address +=
              this.model[addressFieldName + '_suburb_' + field.id].value + ' '
            address +=
              this.model[addressFieldName + '_state_' + field.id].value + ' '
            address +=
              this.model[addressFieldName + '_postcode_' + field.id].value + ' '
            address +=
              this.model[addressFieldName + '_country_' + field.id].value
          }
          data[addressFieldName] = address
        })

      const myAddressField = data['address']

      this.formDef.fields
        .filter(
          (field) =>
            /same_as_my_address.*/.test(field.name) &&
            this.model[field.nameId].value &&
            this.model[field.nameId].value.includes('Same as my address')
        )
        .forEach((field) => {
          const thisAddressFieldName = field.name.replace(
            'same_as_my_address',
            'address'
          )
          data[thisAddressFieldName] = myAddressField
        })

      // set empty array values to null
      Object.entries(data)
        .filter(
          ([fieldName, value]) => Array.isArray(value) && value.length === 0
        )
        .forEach(([fieldName, value]) => {
          data[fieldName] = null
        })

      return data
    },
    async checkValidBeforeSubmit() {
      try {
        this.validateSteppers()
        if (!this.formValid) {
          this.invalidConfirmDialog = true
          const invalidConfirmDialogAnswer = await new Promise(
            (resolve, reject) => {
              this.invalidConfirmDialogResolve = resolve
            }
          )

          return invalidConfirmDialogAnswer
        }

        return true
      } catch (e) {
        this.$showErrorbar(
          'Something went wrong. Refresh your browser and try again.'
        )
        this.$Sentry.captureException(e)
        return false
      }
    },
    async submit(metadata) {
      this.saving = true

      if (!metadata) {
        // TODO BETA make global helpers
        metadata = {
          modified: firebase.firestore.FieldValue.serverTimestamp(),
          modifiedBy: this.$store.state.user.uid,
          progress: this.progress,
          status: 'Editing'
        }
      }

      var data = this.getFormData()
      try {
        var batch = fsdb.batch()
        batch.set(this.documentRef.collection('content').doc('data'), data)
        batch.update(this.documentRef, metadata)
        await batch.commit()
        this.dirty = false
        this.saving = false
      } catch (e) {
        this.saveErrors = this.saveErrors + 1
        this.$showErrorbar(
          'Unable to save. Please check your internet connection.'
        )
        this.$Sentry.captureException(e)
      }

      if (this.saving) {
        this.saving = false
        return false
      } else {
        return true
      }
    },
    /*     async reassign (selectedUser) {
      try {
        await reassignDocument(
          this.documentId,
          this.$store.getters.usersById[this.document.assignedTo],
          this.$store.getters.usersById[selectedUser],
          this.$store.state.user.uid
        )

        this.reassignDialog = false
        if (selectedUser !== this.$store.state.user.uid) {
          this.$router.push('/')
        }
      } catch (e) {
        this.$showErrorbar('Error reassigning document. Refresh and try again.')
        this.$Sentry.captureException(e)
      }
    },
 */
    async sendTo(role) {
      // TODO BETA the below sections should be refactored, and possibly refactored with reassignDocument method
    },
    async sendToClient() {
      this.sending = true

      // not validating as it's expected that the form will be incomplete at this point

      var updates = {
        modified: firebase.firestore.FieldValue.serverTimestamp(),
        modifiedBy: this.$store.state.user.uid,
        stage: 'Client',
        status: 'Sent to Client',
        assignedTo: this.document.client,
        progress: this.progress
      }

      if (await this.submit(updates)) {
        this.$showSuccessbar('Sent.')
        this.$router.push('/')
      }

      this.sending = false
    },

    async sendToAccountTypeUser() {
      this.sending = true

      if (!(await this.checkValidBeforeSubmit())) {
        return
      }

      // TODO MVP need user to be able to select particular FA. Do they? Yeah, because they might have initiated it themselves
      var updates = {
        modified: firebase.firestore.FieldValue.serverTimestamp(),
        modifiedBy: this.$store.state.user.uid,
        stage: 'Client Completed',
        status: 'Sent to ' + _.startCase(this.document.accountType),
        assignedTo:
          this.document.roles[this.document.accountType] ||
          this.document.createdBy,
        progress: this.progress
      }

      if (await this.submit(updates)) {
        this.$showSuccessbar('Sent.')
        this.$router.push('/')
      }

      this.sending = false
    },

    async sendToLawyer() {
      this.reassignToLawyer()
    },

    async reassignToLawyer(lawyerId, status) {
      try {
        throw new Exception(`Someone tried to reassign to lawyer ${lawyerId} with status ${status}`)
      } catch (e) {
        this.$showErrorbar(
          'Unfortunately, that functionality is not yet supported.'
        )
        this.$Sentry.captureException(e)
      }
    },

    async toggleLawyerReviewedSignedOffline() {
      this.saving = true

      var updates = {
        modified: firebase.firestore.FieldValue.serverTimestamp(),
        modifiedBy: this.$store.state.user.uid,
        lawyerReviewedSignedOffline: !this.document.lawyerReviewedSignedOffline
      }

      try {
        await this.documentRef.update(updates)
      } catch (e) {
        // TODO make consistent error message enum
        this.$showErrorbar(
          'Unable to save. Please check your internet connection.'
        )
        this.$Sentry.captureException(e)
      }

      this.saving = false
    },

    async toggleIndividualReviewedSignedOffline() {
      this.saving = true

      var updates = {
        modified: firebase.firestore.FieldValue.serverTimestamp(),
        modifiedBy: this.$store.state.user.uid,
        individualReviewedSignedOffline:
          !this.document.individualReviewedSignedOffline
      }

      try {
        await this.documentRef.update(updates)
      } catch (e) {
        this.$showErrorbar(
          'Unable to save. Please check your internet connection.'
        )
        this.$Sentry.captureException(e)
      }

      this.saving = false
    },

    async toggleUpgradeToLawyerType() {
      // TODO MVP upgrade to lawyer type doc
      // actual upgrade should occur server-side after payment

      this.saving = true

      var updates = {
        modified: firebase.firestore.FieldValue.serverTimestamp(),
        modifiedBy: this.$store.state.user.uid,
        upgradeToLawyerType: this.upgradeToLawyerType
        /* TODO BETA? a) accountType shouldn't be called accountType, should be equiv "docStagesType"
                b) docStagesType should become "clientLawyer" here?
        */
      }

      console.log(updates)

      try {
        await this.documentRef.update(updates)
      } catch (e) {
        this.$showErrorbar(
          'Unable to save. Please check your internet connection.'
        )
        this.$Sentry.captureException(e)
      }

      this.saving = false
    },
    async download() {
      if (!(await this.checkValidBeforeSubmit())) {
        return
      }

      this.downloading = true
      this.isLoading = true

      let data = {
        destination: 'download',
        documentId: this.documentId,
        ownerName: this.clientUser ? this.clientUser.name : ''
      }

      if (
        this.document.accountType === 'singleUser' ||
        this.document.accountType === 'standardBusiness'
      ) {
        data = {
          ...data,
          ownerName: this.appUser ? this.appUser.name : ''
        }
      }

      // TODO BETA doesn't handle cancelling download.
      try {
        const resp = await this.$firePost(
          'documents/generateTo',
          auth,
          data,
          { responseType: 'blob' }
        )
        this.isLoading = false
        this.$router.push('/dashboard')
      } catch (e) {
        let snackText
        if (this.$gnp(e, 'response.data.error')) {
          snackText = 'Error generating document: ' + e.response.data.error
        } else {
          snackText =
            'Error generating document. Refresh your browser and try again.'
        }
        this.isLoading = false
        this.$showErrorbar(snackText)
      }

      this.downloading = false
    },
    async finalise() {
      await this.sendToVault()
      this.$router.push('/vault?documentId=' + this.documentId)
    },

    async sendToVault() {
      if (!(await this.checkValidBeforeSubmit())) {
        return
      }

      this.vaulting = true
      this.isLoading = true

      const data = {
        destination: 'vault',
        documentId: this.documentId,
        ownerName: this.clientUser ? this.clientUser.name : ''
      }

      try {
        await this.$firePost('documents/generateTo', auth, data)
        this.isLoading = false
        this.$showSuccessbar('Sent to Vault.')
      } catch (e) {
        this.$Sentry.captureException(e)

        let snackText = 'Error sending to vault. Try again.'
        if (this.$gnp(e, 'response.data.error')) {
          snackText = 'Error sending to vault: ' + e.response.data.error
        }

        this.isLoading = false
        this.$showErrorbar(snackText)
      }

      this.vaulting = false
    },
    lawyerAssistanceClicked(lawyerId) {
      this.reassignToLawyer(lawyerId, 'Assistance')
    },
    groupify(options) {
      var group = ''
      const numOptions = options.length

      return options.reduce((acc, option, index) => {
        const isGroup = option.label.startsWith('--')
        const isFirstOption = index === 0
        const isLastOption = index === numOptions - 1
        const prevOptionIsGroup =
          !isFirstOption && options[index - 1].label.startsWith('--')
        const nextOptionIsGroup =
          !isLastOption && options[index + 1].label.startsWith('--')

        if (isGroup && !prevOptionIsGroup && !isFirstOption) {
          acc.push({ divider: true })
        }

        if (isGroup) {
          const thisLabel = option.label.replace(/--/g, '')
          group = thisLabel

          if (isLastOption || nextOptionIsGroup) {
            acc.push({ label: thisLabel, value: option.value })
            acc.push({ divider: true })
          } else {
            acc.push({ header: thisLabel })
          }
        } else {
          acc.push({ label: option.label, group: group, value: option.value })
        }
        return acc
      }, [])
    },
    resizeDrawer(event) {
      if (!event || this.$store.state.drawer) {
        requestAnimationFrame(() => {
          if (window.innerWidth >= this.$navMobileBreakpoint) {
            this.showContentsDrawer = true
          } else {
            this.showContentsDrawer = false
          }
        })
      }
    },
    isAssetData() {
      getContentAssetData(this.documentRef).get().then((doc) => {
        const assetData = doc.exists ? doc.data() : []
        const data = Object.keys(assetData).filter(key => assetData[key].length > 0)

        const hasData = data && data.length > 0
        this.customSteppers[ASSET_DATA_STEP].hasError = !hasData
        this.customSteppers[ASSET_DATA_STEP].complete = hasData
      })
    }
  }
}
</script>

<style lang="scss">
.v-application--is-ltr
.template-container
.v-form
.v-stepper--vertical
.v-stepper__content {
  max-width: 100%;
  padding: 0;
  margin: 0;
  border: 0;
}

.template-container {
  h2 {
    color: var(--v-darkslategray-base);
  }

  h3 {
    color: var(--v-accent-base);
    font-weight: 600;
  }

  .v-alert {
    background-color: var(--v-lightblue-base);
    padding: 16px 32px;
  }

  .v-form {
    padding-bottom: 20px;
    width: 100%;

    .v-stepper {
      box-shadow: none;
    }

    fieldset legend {
      width: 0 !important;
    }
  }

  .container-form-group {
    padding-top: 0;
    padding-bottom: 0;
  }

  input[type='text'],
  input[type='email'],
  input[type='date'],
  input[type='tel'] {
    margin-top: 0;
  }

  .v-input {
    input {
      max-height: none;
    }
    .v-input__slot {
      margin-bottom: 3px;
    }
    .v-text-field__details {
      margin-bottom: 5px;
    }
  }

  .v-footer {
    background-color: var(--v-lavendergray-base);
    min-height: 80px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .info--text {
      margin-left: 8px;
      font-size: 1.2em;
      color: var(--v-darkbg-base) !important;

      .v-icon {
        padding-bottom: 5px;
      }
    }

    .nav-btn {
      .v-btn {
        padding: 10px 40px;

        &:first-child {
          margin-right: 15px;
        }
      }
    }
  }
}
</style>
